/* import __COLOCATED_TEMPLATE__ from './trending-topic-card.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */

import Component from '@glimmer/component';
import { action } from '@ember/object';
import type { TrendingTopic } from 'embercom/components/reporting/automation/fin-ai-agent/analyze/topic-trends/trending-topics-data-resources';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import { trackedInLocalStorage } from 'ember-tracked-local-storage';
import md5 from 'blueimp-md5';
import { startSurvey } from 'embercom/lib/intercom-widget-helper';

type FeedbackValue = 'positive' | 'negative';

type FeedbackLocalStorage = {
  [key: string]: FeedbackValue;
};

const FEEDBACK_SURVEY_ID = 50178727;
const LOCAL_STORAGE_KEY = 'trending-topic-card-feedback';

interface Args {
  trendingTopic: TrendingTopic;
  onShowConversationExplorer: (topic: TrendingTopic) => void;
  trackAnalyticsEvent: (action: string, object: string, extraContext: Record<string, any>) => void;
}

export default class TrendingTopicCard extends Component<Args> {
  @service declare intl: IntlService;
  @service declare appService: $TSFixMe;
  @service declare intercomEventService: $TSFixMe;

  @trackedInLocalStorage({
    keyName: LOCAL_STORAGE_KEY,
    defaultValue: '{}',
  })
  feedbackStorage = '{}';

  get appId() {
    return this.appService.app.id;
  }

  get adminId() {
    return this.appService.app.currentAdmin.id;
  }

  get topicName() {
    return this.args.trendingTopic.topicName;
  }

  get subtopicName() {
    return this.args.trendingTopic.subtopicName;
  }

  get submittedFeedbackObject(): FeedbackLocalStorage {
    try {
      return JSON.parse(this.feedbackStorage) as FeedbackLocalStorage;
    } catch {
      return {};
    }
  }

  get hasProvidedFeedback() {
    return !!this.submittedFeedbackObject[this.feedbackLocalStorageKey];
  }

  get feedbackLocalStorageKey() {
    let weekStart = this.args.trendingTopic.weekStart;
    return md5(`${this.appId}-${this.adminId}-${this.topicName}-${this.subtopicName}-${weekStart}`);
  }

  @action
  onFeedback(feedback: FeedbackValue) {
    this.args.trackAnalyticsEvent('feedback_submitted', 'trending_topic_card', {
      topic: this.topicName,
      subtopic: this.subtopicName,
      week_start: this.args.trendingTopic.weekStart,
      feedback,
    });

    startSurvey(FEEDBACK_SURVEY_ID);

    let submittedFeedbackCopy = this.submittedFeedbackObject;
    submittedFeedbackCopy[this.feedbackLocalStorageKey] = feedback;
    this.feedbackStorage = JSON.stringify(submittedFeedbackCopy);
  }

  @action
  onShowConversationExplorer(topic: TrendingTopic) {
    this.args.onShowConversationExplorer(topic);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::FinAiAgent::Analyze::TopicTrends::TrendingTopicCard': typeof TrendingTopicCard;
  }
}
